import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useRouter } from 'expo-router';
import { ActivityIndicator, Pressable, ScrollView, useColorScheme } from 'react-native';
import { Paragraph, YStack } from 'tamagui';

import { Text } from '@/components/text';
import { Heading } from '@/components/heading';
import { PrimaryButton } from '@/components/primary-button';
import { ScreenContainer } from '@/components/screen-container';
import { ScreenHeader } from '@/components/screen-header';
import { TextArea } from '@/components/text-area';
import { api, isReportTargetType, type ReportSeverity, type ReportTargetType } from '@/lib/api';
import { SEVERITY_OPTIONS } from '@/lib/report-severity';
import { borderWidth, darkColors, lightColors, space } from '@/theme/tokens.generated';

const MAX_REPORT_REASON_LEN = 5000; // mirrors backend/internal/domain/report.go's maxReasonLen exactly

// Loose UUID-shape check, not a strict version/variant validator -- this
// only needs to catch "garbage deep link" cases (a missing param, a
// typo, a non-UUID string) before they reach the backend as a raw
// string; the backend's own UUID parsing is still the real validation
// (issue #126 AC6).
const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Reachable from a profile, thread, or post (issue #17's own
// acceptance criterion -- see Task 10 for the three entry points).
// Single confirmation (submitting itself, no separate "are you sure"),
// same shape as src/screens/block-user/index.tsx. Severity choice uses
// SEVERITY_OPTIONS' plain, non-alarming language, not a legalese
// dropdown -- issue #17's explicit acceptance criterion.
export function ReportTarget({
  targetType,
  targetId,
}: {
  targetType: ReportTargetType | undefined;
  targetId: string | undefined;
}) {
  const router = useRouter();
  const [severity, setSeverity] = useState<ReportSeverity>('routine');
  const [reason, setReason] = useState('');
  const [done, setDone] = useState(false);

  const report = useMutation({
    mutationFn: () =>
      api.createReport({
        targetType: targetType as ReportTargetType,
        targetId: targetId as string,
        reason: reason.trim(),
        severity,
      }),
    onSuccess: () => setDone(true),
  });

  const canSubmit = reason.trim() !== '';

  // A garbage deep link (missing param, typo'd targetType, a non-UUID
  // targetId) must render a real error, not "Report this undefined"
  // (issue #126 AC6) -- narrowing on the actual checks (not a separate
  // boolean) so targetType/targetId are real, non-undefined strings
  // below, not the `as` casts the mutation above still has to use.
  if (
    targetType === undefined ||
    !isReportTargetType(targetType) ||
    targetId === undefined ||
    !UUID_SHAPE.test(targetId)
  ) {
    return (
      <ScreenContainer>
        <YStack flex={1} pt="$8" gap="$4">
          <ScreenHeader
            title="Something's missing"
            subtitle="This report link isn't valid. Go back and try again."
            onBack={() => router.back()}
          />
        </YStack>
      </ScreenContainer>
    );
  }

  return (
    <ScreenContainer>
      {/* ScrollView, not a fixed flex={1} YStack -- same structural bug
          class as create-thread/index.tsx's own fix (this session,
          founder-reported live): severity options + a growing TextArea
          + a submit button, all in one non-scrolling column, overflow
          on a short viewport (especially with the keyboard open)
          without scrolling -- it silently overlaps instead. Found by
          auditing every other screen for the same pattern after that
          report, not from a separate live report on this screen
          specifically. */}
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ paddingTop: space[8], paddingBottom: space[6], gap: space[5] }}
        keyboardShouldPersistTaps="handled"
      >
        <ScreenHeader onBack={() => router.back()} />

        {done ? (
          <YStack
            backgroundColor="$feedbackSuccessBg"
            borderColor="$feedbackSuccessBorder"
            borderWidth={borderWidth.hairline}
            borderRadius="$sm"
            p="$4"
            gap="$2"
          >
            <Heading>Report submitted</Heading>
            {/* States what actually happened -- issue #17's explicit
                acceptance criterion. Copy branches on severity since
                the two paths genuinely differ (24h queue vs. immediate
                alert). */}
            <Paragraph color="$feedbackSuccessText">
              {severity === 'severe'
                ? 'Our team has been alerted and will review this right away. Thank you for telling us.'
                : "We'll review this within a day. Thank you for telling us."}
            </Paragraph>
            <PrimaryButton onPress={() => router.push('/')}>Done</PrimaryButton>
          </YStack>
        ) : (
          <YStack gap="$4">
            <Heading>Report this {targetType}</Heading>

            <YStack gap="$2">
              {SEVERITY_OPTIONS.map((option) => (
                <SeverityOption
                  key={option.value}
                  option={option}
                  checked={severity === option.value}
                  onPress={() => setSeverity(option.value)}
                />
              ))}
            </YStack>

            {/* Issue #134 AC3: nothing on this screen previously said
                Nearkin isn't an emergency service, even next to a
                "This needs urgent attention" option a user in real
                danger might reasonably read as a fast-enough channel.
                Always visible (not just when severity is "severe") --
                design.md's own Incident response section: "the app
                must clearly and repeatedly state" this, not gate it
                behind a choice a panicking user might not make first. */}
            <Paragraph color="$colorTertiary" fontSize="$meta" lineHeight="$meta">
              Nearkin isn&apos;t an emergency service. If you or someone else is in immediate danger, contact local
              emergency services directly.
            </Paragraph>

            <TextArea
              value={reason}
              onChangeText={setReason}
              placeholder="What happened?"
              minHeight={100}
              maxLength={MAX_REPORT_REASON_LEN}
            />

            {report.isError && (
              <Paragraph color="$destructive" fontSize="$meta" lineHeight="$meta">
                {report.error.message || 'Something went wrong. Check your connection and try again.'}
              </Paragraph>
            )}

            <PrimaryButton
              disabled={!canSubmit}
              loading={report.isPending}
              onPress={() => {
                if (!report.isPending && canSubmit) report.mutate();
              }}
            >
              {report.isPending ? <ActivityIndicator color="white" /> : 'Submit report'}
            </PrimaryButton>
          </YStack>
        )}
      </ScrollView>
    </ScreenContainer>
  );
}

// Issue #148 AC2: this "radio" option (accessibilityRole="radio") had
// no focus treatment at all -- a keyboard user tabbing through severity
// options got zero visual indication of where focus landed. Real
// outline on focus (`useColorScheme()` + the same light/dark
// `actionPrimary` hex pair PrimaryButton's own `$outlineColor` theme
// entry uses, tamagui.config.shared.ts issue #148 AC1 -- see
// text-link.tsx's own comment for why a raw Pressable needs this
// manual approach rather than Tamagui's own theme-aware outlineColor
// prop) -- keeps this visually consistent with every other real focus
// ring in the app rather than inventing a second value. Own component
// (not inlined in the map callback above) because each option needs
// its own independent focused-state hook -- a shared boolean at the
// list level would highlight every option at once.
function SeverityOption({
  option,
  checked,
  onPress,
}: {
  option: (typeof SEVERITY_OPTIONS)[number];
  checked: boolean;
  onPress: () => void;
}) {
  const [focused, setFocused] = useState(false);
  const colorScheme = useColorScheme();
  const outlineColor = colorScheme === 'dark' ? darkColors.actionPrimary : lightColors.actionPrimary;

  return (
    <Pressable
      accessibilityRole="radio"
      accessibilityState={{ checked }}
      aria-checked={checked}
      onPress={onPress}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={focused ? { outlineStyle: 'solid', outlineWidth: 2, outlineColor, outlineOffset: 2 } : null}
    >
      <YStack
        backgroundColor={checked ? '$card' : 'transparent'}
        borderColor={checked ? '$borderColorStrong' : '$borderColor'}
        borderWidth={borderWidth.hairline}
        borderRadius="$sm"
        p="$4"
        gap="$1"
      >
        <Text color="$color" fontWeight="$semibold">
          {option.label}
        </Text>
        <Text color="$colorSecondary" fontSize="$meta" lineHeight="$meta">
          {option.description}
        </Text>
      </YStack>
    </Pressable>
  );
}
